import { useMemo } from 'react';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import styles from './pick-list-grid.module.less';
import { createActionsColumnDefinition } from './cells/actions-cell';
import { createCreatedColumnDefinition } from './cells/created-cell';
import { createDefaultColumnDefinition } from './cells/default-cell';
import { createLastModifiedByColumnDefinition } from './cells/last-modified-by-cell';
import { createLastModifiedColumnDefinition } from './cells/last-modified-cell';
import { createNameColumnDefinition } from './cells/name-cell';
import { createSizeColumnDefinition } from './cells/size-cell';

import type { Column } from 'react-table';
import type { HtmlTemplate } from '../../../../../components/html-editor/types';

export type PickListGridItem = Omit<HtmlTemplate, 'packingSlipId'> & {
  shared?: boolean;
};

type PickListGridProps = {
  items: PickListGridItem[];
  isLoading: boolean;
  defaultTemplateKey?: string;
  onEditTemplate: (id: string) => void;
  onCopyTemplate: (id: string) => void;
  onDeleteTemplate: (id: string) => void;
};

export const PickListGrid = ({
  items,
  isLoading,
  defaultTemplateKey,
  onEditTemplate,
  onCopyTemplate,
  onDeleteTemplate,
}: PickListGridProps) => {
  const columns = useMemo<Column<PickListGridItem>[]>(
    () =>
      [
        createNameColumnDefinition(),
        createSizeColumnDefinition(),
        createCreatedColumnDefinition(),
        createLastModifiedColumnDefinition(),
        createLastModifiedByColumnDefinition(),
        createDefaultColumnDefinition(defaultTemplateKey),
        createActionsColumnDefinition({ onEditTemplate, onCopyTemplate, onDeleteTemplate }),
      ] as Column<PickListGridItem>[],
    [defaultTemplateKey, onEditTemplate, onCopyTemplate, onDeleteTemplate],
  );

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: items,
    },
    useFlexLayout,
  );

  return (
    <ReactTableWrapper
      className={styles.table}
      getTableBodyProps={getTableBodyProps}
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      headerRowClassName={styles.header}
      isLoading={isLoading}
      prepareRow={prepareRow}
      rows={rows}
    />
  );
};
